<template>
    <div class="process-detail">
        <loader v-if="$loadingRouteData"></loader>

        <div class="container -processes-detail">
            <ol class="breadcrumb">
                <li><a v-link="{name: 'home'}">Accueil</a></li>
                <li><a v-link="{name: 'processes'}">Process</a></li>
                <li v-if="process && process.id">{{ process.name }}</li>
                <li v-if="!process || !process.id">Ajout d'un process</li>
            </ol>

            <div class="header f -wrap -a-center">
                <div class="title expand">
                    <h1><editable-text :value.sync="process.name"
                                       placeholder="Nom du process"></editable-text></h1>
                </div>

                <div>
                    <a v-link="{name: 'processes'}"
                       class="btn btn-link">
                        Annuler
                    </a>
                    <button class="btn btn-primary"
                            @click.prevent="save"
                            :class="valid ? '' : 'disabled'">
                        <loader :inline="true"
                                v-show="saving"></loader>
                        Enregistrer
                    </button>
                </div>
            </div>

            <div class="steps">
                <div class="panel-group">
                    <div class="panel panel-default"
                         v-for="step in process.steps">
                        <div class="panel-heading">
                            <h4 class="panel-title f -gutter -a-center">
                                <a @click.prevent="toggle(step)" href="#">
                                    <i class="fa fa-square-o"
                                       v-if="activeStep === step"></i>
                                    <i class="fa fa-plus-square-o"
                                       v-if="activeStep !== step"></i>
                                </a>

                                <editable-text class="expand"
                                               :value.sync="step.name"
                                               placeholder="Nom de l'étape"></editable-text>

                                <div class="actions">
                                    <a @click.prevent="swap($index, $index - 1)"
                                       v-if="$index > 0"
                                       class="btn btn-primary btn-sm">
                                        <i class="fa fa-arrow-up"></i>
                                    </a>
                                    <a @click.prevent="swap($index, $index + 1)"
                                       v-if="$index < process.steps.length - 1"
                                       class="btn btn-primary btn-sm">
                                        <i class="fa fa-arrow-down"></i>
                                    </a>
                                    <span v-if="$index === process.steps.length - 1"
                                          class="btn btn-primary btn-sm btn-invisible">
                                        <i class="fa fa-arrow-down"></i>
                                    </span>
                                    <a @click.prevent="remove(step)"
                                       class="btn btn-default btn-sm"
                                       :class="{disabled: !step.deletable}">
                                        <i class="fa fa-trash"></i>
                                    </a>
                                </div>
                            </h4>
                        </div>
                        <div class="panel-body"
                             v-if="activeStep === step">
                            <h4>Modèle email</h4>

                            <div class="well">
                                Variables disponibles&nbsp;:

                                <ul>
                                    <li>
                                        <code>[[ENVOI]]</code>
                                        numéro d'envoi
                                    </li>
                                    <li>
                                        <code>[[DOCUMENTS]]</code>
                                        liste des documents à retourner
                                    </li>
                                    <li>
                                        <code>[[DESTINATAIRES]]</code>
                                        liste des destinataires
                                    </li>
                                    <li>
                                        <code>[[COMPAGNIE]]</code>
                                        nom de la compagnie
                                    </li>
                                    <li>
                                        <code>[[TYPE]]</code>
                                        type de contrat
                                    </li>
                                </ul>
                            </div>

                            <div class="row">
                                <div class="col-sm-12">
                                    <label>Durée avant relance</label>
                                    <input type="text"
                                           v-model="step.timeout"
                                           required
                                           class="form-control">
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-sm-12">
                                    <label>Sujet</label>
                                    <input type="text"
                                           v-model="step.email_subject"
                                           required
                                           class="form-control">
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-sm-12">
                                    <label>Corps</label>
                                    <textarea rows="10"
                                              class="form-control"
                                              v-model="step.email_body"></textarea>
                                </div>
                            </div>

                            <h4>Types de documents associés</h4>

                            <div class="row">
                                <div class="col-sm-12">
                                    <div v-for="doc of allDocs">
                                        <label for="doc-{{ $index }}">
                                            <input type="checkbox"
                                                   id="doc-{{ $index }}"
                                                   :value="parseInt(doc.value, 10)"
                                                   v-model="step.document_types"/>
                                            {{ doc.label }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="cta">
                    <a @click.prevent="addStep"
                       class="btn btn-default">
                        <i class="glyphicon glyphicon-plus"></i>
                        Ajouter une étape
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<style type="text/css" scoped>
    .btn-invisible {
        opacity: 0;
        cursor: default;
    }
</style>

<script type="text/ecmascript-6">
    import Loader from '../components/Loader';
    import EditableText from '../components/editable/EditableText';
    import {emptyProcess, emptyState, create, update, fetch} from '../api/processes';
    import {allDocTypeChoices} from '../api/document_types';
    import {asyncAction, d} from '../utils';
    import {manager as msgManager} from '../messages';
    import router from '../router';

    export default {
        components: {
            Loader,
            EditableText
        },
        data() {
            return {
                activeStep: undefined,
                process: emptyProcess(),
                allDocs: [],
                saving: false
            };
        },
        route: {
            data: async function (transition) {
                const curId = d(() => this.process.id);
                const toId = d(() => transition.to.params.id);
                if (curId && curId === toId) {
                    return;
                }

                const out = {};

                try {
                    if (!this.allDocs.length) {
                        out.allDocs = await allDocTypeChoices();
                    }

                    if (toId) {
                        out.process = await fetch(toId);
                    }

                    return out;
                } catch (e) {
                    router.replace({name: 'not-found'});
                }
            }
        },
        computed: {
            valid() {
                if (!this.process) {
                    return false;
                }

                if (!this.process.name) {
                    return false;
                }

                if (!this.process.steps.length) {
                    return false;
                }

                return !this.process.steps.some((s) => !s.name);
            }
        },
        methods: {
            addStep() {
                this.process.steps.push(emptyState());
            },
            swap(a, b) {
                const [av, bv] = [this.process.steps[a], this.process.steps[b]];
                this.process.steps.$set(a, bv);
                this.process.steps.$set(b, av);
            },
            remove(step) {
                if (!step.deletable) {
                    return;
                }

                this.process.steps = this.process.steps.filter((s) => s !== step);
            },
            save: asyncAction('saving', async function () {
                if (!this.valid) {
                    return;
                }

                this.process.steps.forEach((s, i) => Object.assign(s, {order: i}));

                if (!this.process.id) {
                    this.process = await create(this.process);
                    router.replace({
                        name: 'processes-edit',
                        params: {
                            id: this.process.id
                        }
                    });
                    msgManager.push('Succès.', 'Le process a bien été créé.', 'success');
                } else {
                    this.process = await update(this.process);
                    msgManager.push('Succès.', 'Le process a bien été mis à jour.', 'success');
                }
            }),
            toggle(step) {
                if (this.activeStep === step) {
                    this.activeStep = undefined;
                } else {
                    this.activeStep = step;
                }
            }
        }
    };
</script>



// WEBPACK FOOTER //
// ProcessesDetail.vue?17a7cb1f